<template>
  <div class="tabs-container">
    <el-tabs v-model="activeTabName" type="border-card" :closable="isEdit" @tab-remove="handleTabRemove">
      <el-tab-pane v-for="item in usCustomsClearanceListFilter" :key="item._title" :label="item._title" :name="item._title">
        <tab-us-customs-clearance-item :ref="item._title" :ams-port-list="amsPortList" :action="action" :job="job" :form="item" :show-sync="showSync" :cooperate-edit="cooperateEdit" @chargeChange="onChargeChange" @clear="onClear" @copy="onCopy" />
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import tabUsCustomsClearanceItem from './tabUsCustomsClearanceItem'
import lodash from 'lodash'
import moment from 'moment'
import { TabError } from '@/utils/class/TabError'
import { getLoading4Cxt } from '@/utils/utilBaseElement'
import StrUtils from '@/utils/StrUtils'
import ColumnUtils from '@/utils/ColumnUtils'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { ensureExecuteSuccess, getAllSortedRows } from '@/utils/commonUtil'
import { toStatistics, syncAtdInfo, syncAtdInfoAE, syncAtaInfo } from './tabUsCustomsClearanceUtil'
import { UsCustomsClearance } from '@/class/model/booking/UsCustomsClearance'
import { getUsCustomsClearanceDetailList } from '@/api/booking/usCustomsClearance'
import { getSeaAllAmsPort } from '@/api/seaport'
import { getAirAllAmsPort } from '@/api/airport'
import { queryAllUSCommodityCode } from '@/api/usCommodityCode'
import { deepCopy } from '@/utils/commonUtil'

export default {
  name: 'TabUsCustomsClearance',
  components: { tabUsCustomsClearanceItem },
  props: {
    context: { type: Object, required: true },
    action: { type: String, required: true },
    showSync: { type: Boolean, default: true },
    cooperateEdit: { type: Boolean, default: true } // 能否修改
  },
  data() {
    return {
      amsPortList: [],
      activeTabName: '',
      job: {},
      lob: '',
      deletedUsCustomsClearanceKeys: []
    }
  },
  computed: {
    isEdit() {
      if (!this.cooperateEdit) return false
      return this.action !== 'look'
    },
    usCustomsClearanceListFilter() {
      const result = []
      if (this.job.usCustomsClearanceList && this.job.usCustomsClearanceList.length > 0) {
        for (const item of this.job.usCustomsClearanceList) {
          const dataType = item.dataType ? Number(item.dataType) : 0
          if (!dataType) continue
          if (dataType === 1 && (this.$checkPermission(['tab_UsCustomsClearance_customerView']) || this.$checkPermission(['tab_UsCustomsClearance_customerEdit']))) {
            result.push(item)
          } else if (dataType === 2 && (this.$checkPermission(['tab_UsCustomsClearance_reportView']) || this.$checkPermission(['tab_UsCustomsClearance_reportEdit']))) {
            result.push(item)
          } else if (dataType === 3 && (this.$checkPermission(['tab_UsCustomsClearance_secretView']) || this.$checkPermission(['tab_UsCustomsClearance_secretEdit']))) {
            result.push(item)
          }
        }
      }
      return result
    }
  },
  methods: {
    async onShow(row, isRefresh) {
      if (!isRefresh) {
        return
      }
      let { job, lob, amsPortList } = this
      job = row
      const ajaxResults = []
      if (job.lob !== lob) {
        lob = job.lob
        ajaxResults.push(this.loadAmsPortList(lob).then(data => { amsPortList = data }))
      }
      this.job = job
      if (job.uuid) {
        if (job.usCustomsClearanceList && job.usCustomsClearanceList.length > 0) {
          for (const item of job.usCustomsClearanceList) {
            if (item.usCustomsClearanceCargoList) {
              item.usCustomsClearanceCargoList.sort((a, b) => {
                return a.serialNumber - b.serialNumber
              })
              item.deletedUsCustomsClearanceCargoKeys = []
            }
          }
          this.initStatistics()
        } else {
          ajaxResults.push(
            getUsCustomsClearanceDetailList({ jobUuid: job.uuid }).then(res => {
              for (const item of res.data) {
                item.usCustomsClearanceCargoList.sort((a, b) => {
                  return a.serialNumber - b.serialNumber
                })
                item.deletedUsCustomsClearanceCargoKeys = []
              }
              job.usCustomsClearanceList = res.data || []
              if (job.usCustomsClearanceList.length === 0) {
                job.usCustomsClearanceList.push(this.getNewUsCustomsClearance())
              }
              this.generateTabTitles()
              this.initStatistics()
            })
          )
        }
      } else {
        job.usCustomsClearanceList = [this.getNewUsCustomsClearance()]
        this.generateTabTitles()
      }
      if (ajaxResults.length > 0) {
        const loading = getLoading4Cxt(this)
        try {
          await Promise.all(ajaxResults)
        } finally {
          loading.close()
        }
      }
      if (this.job.lob === 'AE') {
        if (this.job.atdChanged) {
          syncAtdInfoAE(this.job.usCustomsClearanceList, this.job.atd)
        }
      } else {
        if (this.job.secondVesselETD) {
          syncAtdInfo(this.job.usCustomsClearanceList, this.job.secondVesselETD)
        } else if (this.job.atd) {
          syncAtdInfo(this.job.usCustomsClearanceList, this.job.atd)
        }
        if (this.job.ataChanged) {
          syncAtaInfo(this.job.usCustomsClearanceList, this.job.ata)
        }
      }
      this.lob = lob
      this.amsPortList = amsPortList
      this.activeTabName = this.job.usCustomsClearanceList[0]._title
      this.deletedUsCustomsClearanceKeys = []
      if (this.isEdit && this.job.uuid) {
        this.$emit('syncData', { usCustomsClearanceList: deepCopy(this.job.usCustomsClearanceList) })
      }
    },
    async loadAmsPortList(lob) {
      if (lob === 'SE' || lob === 'SI') {
        const res = await getSeaAllAmsPort()
        for (const item of res.data) {
          item.code = item.usNo
          delete item.usNo
        }
        return res.data.filter(p => p.status === 1)
      } else if (lob === 'AE' || lob === 'AI') {
        const res = await getAirAllAmsPort()
        return res.data.filter(p => p.status === 1)
      } else {
        return []
      }
    },
    getNewUsCustomsClearance() {
      const newObj = new UsCustomsClearance().fillDefaultValue().getData()
      const todayStr = moment().format('YYYY-MM-DD')
      const isSea = this.job.lob === 'SE' || this.job.lob === 'SI'
      const isAir = this.job.lob === 'AE' || this.job.lob === 'AI'
      const dischargingPortUsCode = this.getCodeOfAmsPort(this.job.dischargingPortCode)
      let loadingPortUsCode = ''
      let loadingPortCountry = ''
      if (this.job.loadingPortCode) {
        loadingPortUsCode = this.getCodeOfAmsPort(this.job.loadingPortCode)
        if (isSea) {
          loadingPortCountry = ColumnUtils.resolveColumnLabel(basicCacheKeys.basic_data_Seaport, 'code', this.job.loadingPortCode, 'countryCode')
        } else if (isAir) {
          loadingPortCountry = ColumnUtils.resolveColumnLabel(basicCacheKeys.basic_data_Airport, 'code', this.job.loadingPortCode, 'countryCode')
        }
      }
      newObj.pdfType = 'PAPERLESS'
      newObj.summaryDate = todayStr
      newObj.entryDate = todayStr
      newObj.portCode = isAir ? '2720' : dischargingPortUsCode
      newObj.entryDate = todayStr
      newObj.importingCarrier = this.job.vessel
      if (isSea) {
        newObj.modeOfTransport = '11'
      } else if (isAir) {
        newObj.modeOfTransport = '40'
      }
      newObj.countryOfOrigin = loadingPortCountry
      newObj.importDate = this.job.ata
      if (newObj.importDate) {
        newObj.entryDate = moment(newObj.importDate).subtract(2, 'days').format('YYYY-MM-DD')
        newObj.summaryDate = moment(newObj.entryDate).add(10, 'days').format('YYYY-MM-DD')
        newObj.signatureDate = newObj.entryDate
      }
      if (this.job.soNo) {
        const strs = this.job.soNo.split('\n')
        let minSoNo = strs[0]
        for (let i = 1; i < strs.length; ++i) {
          if (strs[i].length < minSoNo.length) {
            minSoNo = strs[i]
          } else if (strs[i].length === minSoNo.length) {
            if (strs[i] < minSoNo) {
              minSoNo = strs[i]
            }
          }
        }
        newObj.blAwbNo = minSoNo
      }
      newObj.exportingCountry = loadingPortCountry
      newObj.showChinaSurtax = newObj.exportingCountry === 'CN'
      newObj.exportDate = this.job.atd
      newObj.foreignPortOfLading = loadingPortUsCode
      newObj.usPortOfUnlading = isAir ? '2720' : dischargingPortUsCode
      if (isSea) {
        newObj.locationOfGoods = 'Z773'
        newObj.choose = 'Voyage'
      } else if (isAir) {
        newObj.locationOfGoods = 'E811'
        newObj.choose = 'Flight'
      }
      newObj.code2 = this.job.voyageFlight
      newObj.customerReference = this.job.jobNo
      newObj.totalQuantity = this.job.quantityActual || this.job.quantityContract
      newObj.unit = this.job.packing
      newObj.usCustomsClearanceCargoList = []
      newObj.deletedUsCustomsClearanceCargoKeys = []
      if (this.job.lob === 'AE') {
        newObj.blAwbNo = this.job.mbl
        newObj.importingCarrier = ''
        newObj.code2 = ''
        for (let i = 0; i < this.job.voyageFlight.length; ++i) {
          const char = this.job.voyageFlight[i]
          const code = this.job.voyageFlight.charCodeAt(i)
          if (code >= 65 && code <= 90 || code >= 97 && code <= 122) {
            newObj.importingCarrier += char
          } else if (code >= 48 && code <= 57) {
            newObj.code2 += char
          }
        }
        newObj.entryDate = this.job.atd
        newObj.importDate = this.job.atd
        if (newObj.entryDate) {
          newObj.summaryDate = moment(newObj.entryDate).add(10, 'days').format('YYYY-MM-DD')
        } else {
          newObj.summaryDate = todayStr
        }
      }
      return newObj
    },
    getCodeOfAmsPort(portCode) {
      const amsPort = this.amsPortList.find(p => p.portCode === portCode)
      if (amsPort) {
        return amsPort.code
      }
      return ''
    },
    // 生成页签标题（初始化）
    generateTabTitles() {
      const dataTypeInfos = {
        '1': { name: this.$t('queryJob.CustomerInformation'), num: 0 },
        '2': { name: this.$t('queryJob.DeclarationMaterials'), num: 0 },
        '3': { name: this.$t('queryJob.TopSecretInformation'), num: 0 }
      }
      for (const item of this.job.usCustomsClearanceList) {
        const info = dataTypeInfos[String(item.dataType)]
        info.num += 1
        item._num = info.num
        item._title = info.name
        if (item._num > 1) {
          item._title += item._num
        }
      }
    },
    // 生成页签标题
    generateTabTitle(data) {
      const dataTypeInfos = {
        '1': this.$t('queryJob.CustomerInformation'),
        '2': this.$t('queryJob.DeclarationMaterials'),
        '3': this.$t('queryJob.TopSecretInformation')
      }
      let num = 0
      for (const item of this.job.usCustomsClearanceList) {
        if (item.dataType === data.dataType) {
          if (item._num > num) {
            num = item._num
          }
        }
      }
      num += 1
      data._num = num
      data._title = dataTypeInfos[String(data.dataType)]
      if (data._num > 1) {
        data._title += data._num
      }
    },
    handleTabRemove(targetName) {
      this.$confirm(this.$t('queryJob.DeletionIsUnrecoverable'), this.$t('Message.Prompt'), {
        confirmButtonText: this.$t('common.btn.ok'),
        cancelButtonText: this.$t('common.btn.cancel'),
        closeOnClickModal: false,
        type: 'warning'
      }).then(() => {
        const index = this.job.usCustomsClearanceList.findIndex(p => p._title === targetName)
        const item = this.job.usCustomsClearanceList[index]
        if (item.uuid) {
          this.deletedUsCustomsClearanceKeys.push(item.uuid)
        }
        this.job.usCustomsClearanceList.splice(index, 1)
        if (targetName === this.activeTabName) {
          if (index > 0) {
            this.activeTabName = this.job.usCustomsClearanceList[index - 1]._title
          } else if (index < this.job.usCustomsClearanceList.length) {
            this.activeTabName = this.job.usCustomsClearanceList[index]._title
          } else {
            this.job.usCustomsClearanceList.push(this.getNewUsCustomsClearance())
            this.generateTabTitles()
            this.activeTabName = this.job.usCustomsClearanceList[0]._title
          }
        }
      }).catch(() => { })
    },
    onChargeChange() {
      this.context.reloadCharge = true
    },
    onClear(data) {
      const newObj = this.getNewUsCustomsClearance()
      newObj.dataType = data.dataType
      newObj.deletedUsCustomsClearanceCargoKeys = data.deletedUsCustomsClearanceCargoKeys
      for (const item of data.usCustomsClearanceCargoList) {
        if (item.uuid) {
          newObj.deletedUsCustomsClearanceCargoKeys.push(item.uuid)
        }
      }
      Object.assign(data, newObj)
    },
    onCopy(source, dataType) {
      const newObj = lodash.cloneDeep(source)
      newObj.uuid = ''
      newObj.createdBy = ''
      newObj.dateCreated = ''
      if (typeof dataType === 'number') {
        newObj.dataType = dataType
      }
      for (const item of newObj.usCustomsClearanceCargoList) {
        item.uuid = ''
        item.createdBy = ''
        item.dateCreated = ''
      }
      newObj.deletedUsCustomsClearanceCargoKeys = []
      this.generateTabTitle(newObj)
      this.job.usCustomsClearanceList.push(newObj)
      this.activeTabName = newObj._title
    },
    async initStatistics() {
      const res = await queryAllUSCommodityCode()
      const usCommodityCodeList = res.data.filter(p => p.status === 1)
      for (const item of this.usCustomsClearanceListFilter) {
        const vm = this.$refs[item._title]
        // 确认有值，再往下执行
        await ensureExecuteSuccess(() => vm && (vm.gridApi || vm[0].gridApi))
        toStatistics(getAllSortedRows(vm.gridApi || vm[0].gridApi), usCommodityCodeList, item, this.job.lob)
      }
    },
    async getSubmitData() {
      let currentItem = null
      let chinaAdditionalTaxRateIsEmptyConfirm = false
      try {
        for (const item of this.job.usCustomsClearanceList) {
          currentItem = item
          item.filerCode = StrUtils.toHalfword(item.filerCode.trim())
          item.entryType = StrUtils.toHalfword(item.entryType.trim())
          if (!item.summaryDate) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 3. Summary Date', 'tabUsCustomsClearance')
          }
          item.code = StrUtils.toHalfword(this.filterSpecialCharacters(item.code.trim()))
          item.suretyNo = StrUtils.toHalfword(this.filterSpecialCharacters(item.suretyNo.trim()))
          item.bondType = StrUtils.toHalfword(this.filterSpecialCharacters(item.bondType.trim()))
          if (!item.portCode) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 6. Port Code', 'tabUsCustomsClearance')
          }
          if (!item.entryDate) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 7. Entry Date', 'tabUsCustomsClearance')
          }
          item.importingCarrier = StrUtils.toHalfword(item.importingCarrier.trim())
          if (!item.importingCarrier) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 8. Importing Carrier', 'tabUsCustomsClearance')
          }
          if (!item.importDate) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 11. Import Date', 'tabUsCustomsClearance')
          }
          item.blAwbNo = StrUtils.toHalfword(item.blAwbNo.trim())
          if (!item.blAwbNo) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 12. B/L or AWB No', 'tabUsCustomsClearance')
          }
          item.manufacturerId = StrUtils.toHalfword(item.manufacturerId.trim())
          if (!item.exportDate) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 15. Export Date', 'tabUsCustomsClearance')
          }
          item.itNo = StrUtils.toHalfword(item.itNo.trim())
          item.missingDocs = StrUtils.toHalfword(item.missingDocs.trim())
          if (!item.usPortOfUnlading) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 20. U.S. Port of Unlading', 'tabUsCustomsClearance')
          }
          item.locationOfGoods = StrUtils.toHalfword(item.locationOfGoods.trim())
          if (!item.locationOfGoods) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 21. Location of Goods/G.O. No', 'tabUsCustomsClearance')
          }
          item.code2 = StrUtils.toHalfword(item.code2.trim())
          if (!item.code2) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 21.2. - Code', 'tabUsCustomsClearance')
          }
          item.consigneeNo = StrUtils.toHalfword(item.consigneeNo.trim())
          item.importerNo = StrUtils.toHalfword(item.importerNo.trim())
          item.referenceNo = StrUtils.toHalfword(item.referenceNo.trim())
          item.ultimateConsigneeNameAndAddress = StrUtils.toHalfword(item.ultimateConsigneeNameAndAddress.trim())
          item.address = StrUtils.toHalfword(item.address.trim())
          item.addressDetail = StrUtils.toHalfword(item.addressDetail.trim())
          item.destination = StrUtils.toHalfword(item.destination.trim())
          item.customerReference = StrUtils.toHalfword(item.customerReference.trim())
          if (!item.customerReference) {
            throw new TabError(this.$t('CargoMsg.Cargo_PlsInput') + ' 25.4. - Customer Reference#', 'tabUsCustomsClearance')
          }
          item.city = StrUtils.toHalfword(item.city.trim())
          item.state = StrUtils.toHalfword(item.state.trim())
          item.zip = StrUtils.toHalfword(item.zip.trim())
          item.importerOfRecordNameAndAddress = StrUtils.toHalfword(item.importerOfRecordNameAndAddress.trim())
          item.address2 = StrUtils.toHalfword(item.address2.trim())
          item.addressDetail2 = StrUtils.toHalfword(item.addressDetail2.trim())
          item.city2 = StrUtils.toHalfword(item.city2.trim())
          item.state2 = StrUtils.toHalfword(item.state2.trim())
          item.zip2 = StrUtils.toHalfword(item.zip2.trim())
          for (let j = 0; j < item.usCustomsClearanceCargoList.length; ++j) {
            const subItem = item.usCustomsClearanceCargoList[j]
            if (!subItem.customerProductName) {
              throw new TabError(this.$t('queryJob.PleaseEnterOnLine').replace('{0}', j + 1).replace('{1}', this.$t('queryJob.CustomerName')), 'tabUsCustomsClearance')
            }
            if (!subItem.customsClearanceProductName) {
              throw new TabError(this.$t('queryJob.PleaseEnterOnLine').replace('{0}', j + 1).replace('{1}', this.$t('queryJob.CustomsClearanceProductName')), 'tabUsCustomsClearance')
            }
            if (!subItem.packageType) {
              throw new TabError(this.$t('queryJob.PleaseEnterOnLine').replace('{0}', j + 1).replace('{1}', this.$t('SeaExport.PackageType')), 'tabUsCustomsClearance')
            }
            if (!subItem.netWeight) {
              throw new TabError(this.$t('queryJob.PleaseEnterOnLine').replace('{0}', j + 1).replace('{1}', this.$t('SeaExport.NetWeight')), 'tabUsCustomsClearance')
            }
            if (!subItem.grossWeight) {
              throw new TabError(this.$t('queryJob.PleaseEnterOnLine').replace('{0}', j + 1).replace('{1}', this.$t('SeaExport.GrossWeight')), 'tabUsCustomsClearance')
            }
            if (!subItem.goodsNumber) {
              throw new TabError(this.$t('queryJob.PleaseEnterOnLine').replace('{0}', j + 1).replace('{1}', this.$t('queryJob.NumberOfGoods')), 'tabUsCustomsClearance')
            }
            if (!subItem.customsClearanceUnit) {
              throw new TabError(this.$t('queryJob.PleaseEnterOnLine').replace('{0}', j + 1).replace('{1}', this.$t('queryJob.CustomsClearanceUnit')), 'tabUsCustomsClearance')
            }
            if (!chinaAdditionalTaxRateIsEmptyConfirm && !Number(subItem.chinaAdditionalRate)) {
              chinaAdditionalTaxRateIsEmptyConfirm = true
              await this.$confirm(this.$t('CargoMsg.ChinaAdditionalTaxRateIsEmpty'), this.$t('Message.Prompt'), {
                confirmButtonText: this.$t('Message.Confirm'),
                cancelButtonText: this.$t('Message.Cancel'),
                closeOnClickModal: false,
                type: 'info'
              })
            }
          }
        }
      } catch (err) {
        if (err instanceof TabError) {
          this.activeTabName = currentItem._title
        }
        throw err
      }
      return {
        usCustomsClearanceList: this.job.usCustomsClearanceList,
        deletedUsCustomsClearanceKeys: this.deletedUsCustomsClearanceKeys
      }
    },
    // 过滤特殊字符
    filterSpecialCharacters(str) {
      let result = ''
      for (let i = 0; i < str.length; ++i) {
        const c = str.charAt(i)
        if (StrUtils.isContainSpecialCharacter(c)) {
          continue
        }
        result += c
      }
      return result
    },
    // 组装打印参数
    getPrintData() {
      if (!this.activeTabName) return
      const params = this.$refs[this.activeTabName][0].getPrintData()
      return params
    }
  }
}
</script>

<style scoped lang="scss">
#app .app-main-content .details-view .tabs-container ::v-deep {
  padding: 10px 0 0 15px;
  .el-tabs--border-card {
    background-color: unset;
    border: unset;
    box-shadow: unset;
    > .el-tabs__header {
      background-color: transparent;
      border-bottom: 1px solid #c4c8d0 !important;
      .el-tabs__item {
        background-color: transparent;
        border: 1px solid #c4c8d0 !important;
        box-shadow: unset;
        color: #666666;
        margin: 1px 10px 0 0;
        &.is-closable {
          padding-left: 15px !important;
          span.el-icon-close {
            padding: 3px;
            font-size: 16px;
            margin-top: -3px;
          }
        }
        &.is-active {
          background-color: #ffffff;
          border-bottom: 1px solid #ffffff !important;
          color: black;
        }
      }
    }
  }
}
</style>
